// PortfolioGrid.jsx
// DAS-1635: Grid view component for portfolio positions showing name, ticker, and exposure %
// DAS-1642: Added sorting options (exposure, 1d performance, 1w performance)
// DAS-2095: Added PDF and CSV export buttons
// DAS-2176: Added 1M and YTD performance sort options

import React, { useState, useEffect, useRef } from 'react';
import { Card, Tag, Alert, Select, Space, Button, message } from 'antd';
import { FilePdfOutlined, FileExcelOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import api from '../functions/api';
import ThreeTablesModal from './SearchResultModal';
import getUserId from '../functions/getUserId';
import '../css/PortfolioGrid.css';

const { Option } = Select;

const PortfolioGrid = () => {
  const portfolioData = useSelector(state => state?.portfolio.data);
  const [sortBy, setSortBy] = useState('exposure');
  const [searchResultModal, setSearchResultModal] = useState({ open: false, ticker: null });
  const [searchValue, setSearchValue] = useState('');
  const [searchOptions, setSearchOptions] = useState([]);
  const [selectedTicker, setSelectedTicker] = useState(null);
  const gridRef = useRef(null);

  // Scroll to highlighted card when selectedTicker changes
  useEffect(() => {
    if (selectedTicker) {
      setTimeout(() => {
        const cardElement = document.querySelector(`[data-ticker="${selectedTicker}"]`);
        if (cardElement) {
          cardElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }, 100);
    }
  }, [selectedTicker]);

  if (!portfolioData || !portfolioData.exposures || portfolioData.exposures.length === 0) {
    return (
      <Alert
        message="No portfolio data available"
        description="Please refresh your portfolio data to see positions."
        type="info"
      />
    );
  }

  // Combine portfolio data with exposure data and performance data
  // DAS-1642: Performance data is now included in exposures from the backend
  // Create a map of ticker to Overall_Relative_Performance from portfolio data
  const portfolioMap = {};
  if (portfolioData.portfolio && Array.isArray(portfolioData.portfolio)) {
    portfolioData.portfolio.forEach(item => {
      if (item.Ticker) {
        portfolioMap[item.Ticker.toUpperCase()] = item;
      }
    });
    // DAS-1871: Debug - check what fields are in portfolio items
    if (portfolioData.portfolio.length > 0) {
      const sampleItem = portfolioData.portfolio[0];
      console.log('[DAS-1871] Sample portfolio item keys:', Object.keys(sampleItem));
      console.log('[DAS-1871] Sample portfolio item has Marketcap?', 'Marketcap' in sampleItem);
      console.log('[DAS-1871] Sample portfolio item.Marketcap:', sampleItem.Marketcap);
      console.log('[DAS-1871] Full sample item:', sampleItem);
    }
  }

  let positions = portfolioData.exposures
    .filter(exposure => exposure.ticker && exposure.total_amount_exposure_perc)
    .map(exposure => {
      const portfolioItem = portfolioMap[exposure.ticker.toUpperCase()];
      // DAS-1871: Marketcap from portfolio item (if user has Marketcap in UserPreferences)
      const marketcapValue = portfolioItem && portfolioItem.Marketcap !== null && portfolioItem.Marketcap !== undefined
        ? parseFloat(portfolioItem.Marketcap)
        : null;
      
      return {
        ticker: exposure.ticker,
        name: exposure.company || exposure.ticker,
        exposurePercent: parseFloat(exposure.total_amount_exposure_perc) || 0,
        oneDayPerf: exposure.one_day_perf !== null && exposure.one_day_perf !== undefined 
          ? parseFloat(exposure.one_day_perf) 
          : null,
        oneWeekPerf: exposure.one_week_performance !== null && exposure.one_week_performance !== undefined 
          ? parseFloat(exposure.one_week_performance) 
          : null,
        oneMonthPerf: exposure.one_month_performance !== null && exposure.one_month_performance !== undefined
          ? parseFloat(exposure.one_month_performance)
          : null,
        ytdPerf: exposure.YTD !== null && exposure.YTD !== undefined
          ? parseFloat(exposure.YTD)
          : null,
      roa: exposure.roa !== null && exposure.roa !== undefined 
        ? parseFloat(exposure.roa) 
        : null,
        overallRelativePerf: portfolioItem && portfolioItem.Overall_Relative_Performance !== null && portfolioItem.Overall_Relative_Performance !== undefined
          ? parseFloat(portfolioItem.Overall_Relative_Performance)
          : null,
        fundamentalTrendScore: exposure.fundamental_trend_score !== null && exposure.fundamental_trend_score !== undefined
          ? parseFloat(exposure.fundamental_trend_score)
          : null,
        marketcap: marketcapValue,
        // DAS-1963: Return Probability and Expected Q Return
        returnProbability: exposure.return_probability !== null && exposure.return_probability !== undefined
          ? parseFloat(exposure.return_probability)
          : null,
        expectedQReturn: exposure.expected_q_return !== null && exposure.expected_q_return !== undefined
          ? parseFloat(exposure.expected_q_return)
          : null,
        regime: exposure.regime || null,
      };
    });

  // Search functionality - reuse TickerSearch logic
  const handleSearch = async (value) => {
    setSearchValue(value);
    if (!value) {
      setSearchOptions([]);
      return;
    }

    try {
      const response = await api.get(`/search_suggestions?query=${value}`);
      const autoCompleteOptions = response.data.map((suggestion) => ({
        label: suggestion,
      }));
      setSearchOptions(autoCompleteOptions);
    } catch (error) {
      console.error('Error fetching search suggestions:', error);
      setSearchOptions([]);
    }
  };

  const handleTickerSelect = (event, value) => {
    if (value) {
      const ticker = value.label.split(' - ')[0];
      setSelectedTicker(ticker);
      setSearchValue('');
      setSearchOptions([]);
    }
  };

  // Sort-agnostic sorting logic
  const sortPositions = (positions, sortField) => {
    return [...positions].sort((a, b) => {
      let valA, valB;
      
      switch (sortField) {
        case 'exposure':
          valA = a.exposurePercent;
          valB = b.exposurePercent;
          break;
        case '1d':
          valA = a.oneDayPerf;
          valB = b.oneDayPerf;
          break;
        case '1w':
          valA = a.oneWeekPerf;
          valB = b.oneWeekPerf;
          break;
        case '1m':
          valA = a.oneMonthPerf;
          valB = b.oneMonthPerf;
          break;
        case 'ytd':
          valA = a.ytdPerf;
          valB = b.ytdPerf;
          break;
        case 'roa':
          valA = a.roa;
          valB = b.roa;
          break;
        case 'overallRelativePerf':
          valA = a.overallRelativePerf;
          valB = b.overallRelativePerf;
          break;
        case 'fundamentalTrendScore':
          valA = a.fundamentalTrendScore;
          valB = b.fundamentalTrendScore;
          break;
        case 'marketcap':
          valA = a.marketcap;
          valB = b.marketcap;
          break;
        // DAS-1963: Return Probability and Expected Q Return sorting
        case 'returnProbability':
          valA = a.returnProbability;
          valB = b.returnProbability;
          break;
        case 'expectedQReturn':
          valA = a.expectedQReturn;
          valB = b.expectedQReturn;
          break;
        default:
          valA = a.exposurePercent;
          valB = b.exposurePercent;
      }
      
      // Handle null values - put them at the end
      if (valA === null || valA === undefined) return 1;
      if (valB === null || valB === undefined) return -1;
      
      // Sort descending (highest first)
      return valB - valA;
    });
  };

  positions = sortPositions(positions, sortBy);

  if (positions.length === 0) {
    return (
      <Alert
        message="No positions with exposure data"
        description="Exposure percentages are not available for your portfolio positions."
        type="info"
      />
    );
  }

  // Get the value to display based on sort field
  const getDisplayValue = (position) => {
    switch (sortBy) {
      case 'exposure':
        return { label: 'Exposure', value: `${position.exposurePercent.toFixed(2)}%` };
      case '1d':
        const dayVal = position.oneDayPerf !== null ? position.oneDayPerf.toFixed(2) : 'N/A';
        return { label: '1D Performance', value: `${dayVal}%` };
      case '1w':
        const weekVal = position.oneWeekPerf !== null ? position.oneWeekPerf.toFixed(2) : 'N/A';
        return { label: '1W Performance', value: `${weekVal}%` };
      case '1m':
        const monthVal = position.oneMonthPerf !== null ? position.oneMonthPerf.toFixed(2) : 'N/A';
        return { label: '1M Performance', value: `${monthVal}%` };
      case 'ytd':
        const ytdVal = position.ytdPerf !== null ? position.ytdPerf.toFixed(2) : 'N/A';
        return { label: 'YTD Performance', value: `${ytdVal}%` };
      case 'roa':
        const roaVal = position.roa !== null ? position.roa.toFixed(2) : 'N/A';
        return { label: 'ROA', value: `${roaVal}%` };
      case 'overallRelativePerf':
        const relPerfVal = position.overallRelativePerf !== null ? position.overallRelativePerf.toFixed(2) : 'N/A';
        return { label: 'Overall Relative Performance', value: `${relPerfVal}%` };
      case 'fundamentalTrendScore':
        // Calculate percentage: (score / 29) * 100, where MAX_SCORE = 29
        const MAX_SCORE = 29;
        const trendScoreVal = position.fundamentalTrendScore !== null 
          ? ((position.fundamentalTrendScore / MAX_SCORE) * 100).toFixed(2)
          : 'N/A';
        return { label: 'Fundamental Trend Score', value: `${trendScoreVal}%` };
      case 'marketcap':
        // Format marketcap: if >= 1B show in billions, else show in millions
        let marketcapVal = 'N/A';
        if (position.marketcap !== null && position.marketcap !== undefined) {
          const marketcap = position.marketcap;
          if (marketcap >= 1000) {
            marketcapVal = `$${(marketcap / 1000).toFixed(2)}B`;
          } else {
            marketcapVal = `$${marketcap.toFixed(2)}M`;
          }
        }
        return { label: 'Market Cap', value: marketcapVal };
      // DAS-1963: Return Probability and Expected Q Return display
      case 'returnProbability':
        const probVal = position.returnProbability !== null ? position.returnProbability.toFixed(1) : 'N/A';
        return { label: 'Return Probability', value: `${probVal}%` };
      case 'expectedQReturn':
        const expQVal = position.expectedQReturn !== null ? position.expectedQReturn.toFixed(2) : 'N/A';
        const sign = position.expectedQReturn > 0 ? '+' : '';
        return { label: 'Expected Q Return', value: `${sign}${expQVal}%` };
      default:
        return { label: 'Exposure', value: `${position.exposurePercent.toFixed(2)}%` };
    }
  };

  // DAS-2095: PDF download — captures the grid visually with multi-page support
  const handleDownloadPDF = async () => {
    if (!gridRef.current || positions.length === 0) {
      message.error('No grid content to export');
      return;
    }

    message.loading('Generating PDF...', 0);

    try {
      const element = gridRef.current;

      // Expand all scrollable ancestors (Ant Design Modal body, TabPane, etc.)
      // so html2canvas can capture the full grid, not just the visible viewport
      const savedStyles = [];
      let ancestor = element.parentElement;
      while (ancestor) {
        const cs = window.getComputedStyle(ancestor);
        if (cs.overflow !== 'visible' || cs.maxHeight !== 'none') {
          savedStyles.push({
            el: ancestor,
            overflow: ancestor.style.overflow,
            maxHeight: ancestor.style.maxHeight,
            height: ancestor.style.height,
          });
          ancestor.style.overflow = 'visible';
          ancestor.style.maxHeight = 'none';
          ancestor.style.height = 'auto';
        }
        if (ancestor.classList.contains('ant-modal-wrap')) break;
        ancestor = ancestor.parentElement;
      }

      element.style.overflow = 'visible';
      element.style.height = 'auto';

      await new Promise(resolve => setTimeout(resolve, 300));

      const fullWidth = element.scrollWidth || element.offsetWidth;
      const fullHeight = element.scrollHeight || element.offsetHeight;

      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        width: fullWidth,
        height: fullHeight,
        windowWidth: fullWidth,
        windowHeight: fullHeight,
        scrollX: 0,
        scrollY: 0,
      });

      element.style.overflow = '';
      element.style.height = '';
      savedStyles.forEach(({ el, overflow, maxHeight, height }) => {
        el.style.overflow = overflow;
        el.style.maxHeight = maxHeight;
        el.style.height = height;
      });

      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'a4',
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const titleHeight = 30;
      const usableHeight = pdfHeight - 2 * margin - titleHeight;
      const usableWidth = pdfWidth - 2 * margin;

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const widthRatio = usableWidth / imgWidth;
      const scaledTotalHeight = imgHeight * widthRatio;
      const totalPages = Math.ceil(scaledTotalHeight / usableHeight);
      const sourceHeightPerPage = usableHeight / widthRatio;

      const currentDate = new Date().toISOString().split('T')[0];
      const sortLabelMap = { '1d': '1D_Perf', '1w': '1W_Perf', '1m': '1M_Perf', 'ytd': 'YTD_Perf' };
      const sortLabel = sortLabelMap[sortBy] || sortBy;
      const filename = `Portfolio_Grid_${sortLabel}_${currentDate}.pdf`;

      for (let page = 0; page < totalPages; page++) {
        if (page > 0) pdf.addPage();

        pdf.setFontSize(14);
        pdf.setTextColor(51, 51, 51);
        pdf.text(
          `Portfolio Grid — Sorted by ${sortLabel} — Page ${page + 1}/${totalPages}`,
          margin,
          margin + 12
        );

        const sourceY = page * sourceHeightPerPage;
        const sourceHeight = Math.min(sourceHeightPerPage, imgHeight - sourceY);
        if (sourceHeight <= 0) break;

        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = imgWidth;
        pageCanvas.height = Math.ceil(sourceHeight);
        const ctx = pageCanvas.getContext('2d');
        ctx.drawImage(canvas, 0, sourceY, imgWidth, sourceHeight, 0, 0, imgWidth, sourceHeight);

        const pageImgData = pageCanvas.toDataURL('image/png');
        const destHeight = sourceHeight * widthRatio;
        pdf.addImage(pageImgData, 'PNG', margin, margin + titleHeight, usableWidth, destHeight);
      }

      pdf.save(filename);
      message.destroy();
      message.success(`PDF downloaded: ${filename} (${totalPages} page${totalPages > 1 ? 's' : ''})`);
    } catch (err) {
      message.destroy();
      message.error('Failed to generate PDF');
      console.error('PDF generation error:', err);
    }
  };

  // DAS-2095: CSV download — exports all position data as a spreadsheet-friendly file
  const handleDownloadCSV = () => {
    if (positions.length === 0) {
      message.error('No data to export');
      return;
    }

    const MAX_SCORE = 29;
    const headers = [
      'Rank', 'Ticker', 'Company', 'Exposure %',
      '1D Performance %', '1W Performance %', '1M Performance %',
      'YTD Performance %', 'ROA %',
      'Overall Relative Perf %', 'Fundamental Trend Score %',
      'Market Cap', 'Return Probability %', 'Expected Q Return %', 'Regime',
    ];

    const rows = positions.map((pos, i) => [
      i + 1,
      pos.ticker,
      `"${(pos.name || '').replace(/"/g, '""')}"`,
      pos.exposurePercent != null ? pos.exposurePercent.toFixed(2) : '',
      pos.oneDayPerf != null ? pos.oneDayPerf.toFixed(2) : '',
      pos.oneWeekPerf != null ? pos.oneWeekPerf.toFixed(2) : '',
      pos.oneMonthPerf != null ? pos.oneMonthPerf.toFixed(2) : '',
      pos.ytdPerf != null ? pos.ytdPerf.toFixed(2) : '',
      pos.roa != null ? pos.roa.toFixed(2) : '',
      pos.overallRelativePerf != null ? pos.overallRelativePerf.toFixed(2) : '',
      pos.fundamentalTrendScore != null ? ((pos.fundamentalTrendScore / MAX_SCORE) * 100).toFixed(2) : '',
      pos.marketcap != null ? pos.marketcap.toFixed(2) : '',
      pos.returnProbability != null ? pos.returnProbability.toFixed(1) : '',
      pos.expectedQReturn != null ? pos.expectedQReturn.toFixed(2) : '',
      pos.regime || '',
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');

    const currentDate = new Date().toISOString().split('T')[0];
    const sortLabelMap = { '1d': '1D_Perf', '1w': '1W_Perf', '1m': '1M_Perf', 'ytd': 'YTD_Perf' };
    const sortLabel = sortLabelMap[sortBy] || sortBy;
    a.href = url;
    a.download = `Portfolio_Grid_${sortLabel}_${currentDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    message.success(`CSV downloaded: ${a.download}`);
  };

  return (
    <div className="portfolio-grid">
      <div className="sort-controls">
        <Space>
          <span>Sort by:</span>
          <Select
            value={sortBy}
            onChange={setSortBy}
            style={{ width: 220 }}
          >
            <Option value="exposure">Exposure %</Option>
            <Option value="1d">1 Day Performance</Option>
            <Option value="1w">1 Week Performance</Option>
            <Option value="1m">1 Month Performance</Option>
            <Option value="ytd">YTD Performance</Option>
            <Option value="roa">ROA</Option>
            <Option value="overallRelativePerf">Overall Relative Performance</Option>
            <Option value="fundamentalTrendScore">Fundamental Trend Score</Option>
            <Option value="marketcap">Market Cap</Option>
            <Option value="returnProbability">Return Probability</Option>
            <Option value="expectedQReturn">Expected Q Return</Option>
          </Select>
        </Space>
        <Space className="grid-export-buttons">
          <Button
            icon={<FilePdfOutlined />}
            onClick={handleDownloadPDF}
          >
            Download PDF
          </Button>
          <Button
            icon={<FileExcelOutlined />}
            onClick={handleDownloadCSV}
          >
            Download CSV
          </Button>
        </Space>
        <Box sx={{ flex: 1, maxWidth: 300, marginLeft: 'auto' }}>
          <Autocomplete
            freeSolo
            disableClearable
            options={searchOptions}
            inputValue={searchValue}
            onInputChange={(event, newInputValue) => handleSearch(newInputValue)}
            onChange={handleTickerSelect}
            filterOptions={(x) => x} // disable default filtering
            sx={{
              minWidth: 180,
              "& .MuiOutlinedInput-root": {
                padding: "4px 8px",
                fontSize: "13px",
                height: 32,
                borderRadius: 2,
                boxShadow: 8,
                backgroundColor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="ticker or stockname"
                size="small"
              />
            )}
          />
        </Box>
      </div>
      
      <div className="grid-container" ref={gridRef}>
        {positions.map((position, index) => {
          const display = getDisplayValue(position);
          const rank = index + 1;
          const total = positions.length;
          const isHighlighted = selectedTicker && 
            position.ticker.toLowerCase() === selectedTicker.toLowerCase();
          
          return (
            <Card
              key={`${position.ticker}-${index}`}
              data-ticker={position.ticker}
              className={`position-card ${isHighlighted ? 'position-card-highlighted' : ''}`}
              hoverable
              size="small"
              onClick={() => setSearchResultModal({ open: true, ticker: position.ticker })}
              style={{ cursor: 'pointer' }}
            >
              <div className="position-content">
                <div className="position-header">
                  <div className="position-ticker">
                    <Tag color="blue">{position.ticker}</Tag>
                  </div>
                  <div className="position-rank">
                    {rank}/{total}
                  </div>
                </div>
                <div className="position-name" title={position.name}>
                  {position.name}
                </div>
                <div className="position-exposure">
                  <span className="exposure-label">{display.label}:</span>
                  <span className="exposure-value">{display.value}</span>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
      
      {/* SearchResultModal for ticker details */}
      {searchResultModal.open && (
        <ThreeTablesModal
          tickerValue={searchResultModal.ticker}
          open={searchResultModal.open}
          onClose={() => setSearchResultModal({ open: false, ticker: null })}
          userId={getUserId()}
        />
      )}
    </div>
  );
};

export default PortfolioGrid;



